import React, { lazy, Suspense, useState } from "react"
import useDeepCompareEffect from 'use-deep-compare-effect'

import {
  dataStore,
  filterStore,
  locationStore,
  pageStore
} from "state/store-zustand";

import { getPosts } from 'vibemap-constants/dist/wordpress.js'

import CardSkeletons from "components/post/singCard/cardSkeletons"

const Slider = lazy(() => import('components/slider'))
const PostSingCard = lazy(() => import('components/post/singCard/postCard'))

import PlaceholderImage from '../../../../images/no_image_placeholder.png'
import { MdArrowForward } from '@react-icons/all-files/md/MdArrowForward'

import './postCardsBlock.scss'

const PostCardsBlock = ({ block, searchByVibes = false }) => {
  // Use context
  // Pass city filter to guides
  let filters = filterStore(state => state.filters)
  const hasFilters = filterStore(state => state.hasFilters)
  const isMobile = pageStore(state => state.isMobile)
  const currentCity = locationStore(state => state.cityCurrent)
  const postsFromContext = dataStore(state => state.posts)

  const { overrideQuery } = block

  // Pass through posts from page
  const hasPostsFromContext = postsFromContext && postsFromContext.length > 0
  const hasPostsFromBlock = block.posts && block.posts.length > 0
  const hasInitialPosts = hasPostsFromContext || hasPostsFromBlock

  const city = overrideQuery?.cities && overrideQuery.cities.length > 0
    ? overrideQuery.cities.find(city => city !== undefined)
    : currentCity
      ? currentCity?.slug
      : null

  if (city) {
    filters.cities = [city]
  }

  const initalPosts = hasPostsFromContext
    ? postsFromContext.map((post) => cardFromPost(post, city))
    : hasPostsFromBlock
      ? block.posts.map((post) => cardFromPost(post, city))
      : []

  const [posts, setPosts] = useState(initalPosts)
  const [isLoading, setIsLoading] = useState(true)
  const numPosts = 20

  useDeepCompareEffect(() => {

    (async () => {
      // If cards are prelaoded and fitlers haven't changed, don't fetch
      if (hasInitialPosts && !hasFilters) {
        setIsLoading(false)
        return false
      }

      // TODO: Only show is loading if there are no cards or a new filters
      if (posts.length == 0) setIsLoading(true)

      // If has posts
      if (block.posts && block.posts.length > 0) {
        const posts = block.posts.map((post) => cardFromPost(post, city))
        setPosts(posts)
      }

      // Override fields required
      let fields = [
        'id',
        'date',
        'slug',
        'status',
        'type',
        'link',
        'title',
        'content',
        'author',
        'categories',
        'vibe',
        'blocks',
        'acf',
        'featured_media',
        'featured_media_src_url',
      ]

      // If automatic
      if (block.automatic) {
        //console.log('TODO: include category and vibe filter ', filters)
        // TODO: Add general overrides to filters util
        if (block.overrideQuery) {
          if (block.overrideQuery.vibes && block.overrideQuery.vibes.length > 0) {
            filters.vibes = block.overrideQuery.vibes.map(vibe => vibe.replace('-', ' '))
          }
        }

        // FIXME: Hack to set the city filter
        if (filters?.cities && filters.cities.length < 0) {
          filters.cities = [city]
        }

        if (searchByVibes == false) {
          delete filters.vibes
        }

        console.log('DEBUG: get posts ', filters);

        const response = await getPosts(filters, false, numPosts, fields)
          .catch(error => console.log('Error getting posts ', error))

        if (response && response.data.length > 0) {
          // Exclude self or other posts from related
          const postsFiltered = block.overrideQuery && block.overrideQuery.excludedPosts
            ? response.data.filter(post => !block.overrideQuery.excludedPosts.includes(post.id))
            : response.data

          const posts = postsFiltered.map((post) => cardFromPost(post, city))
          setPosts(posts)
        }
      }
      //setTotalPages(response.numPages)
      setIsLoading(false)
    })()

  }, [setPosts, filters, currentCity, city])

  const hash = block.heading && (
    block.heading.replace(/[^\w\s]|_/g, "").replace(/\s/g, "-")
  )

  let searchQuery = typeof window !== 'undefined' ? window.location.search : ''

  const showAllText = `All guides`

  const cardsLoading = <CardSkeletons num={isMobile ? 1 : 3} horizontal={false} />

  // Don't show the section if there are no results
  if (posts.length == 0) {
    return null
  }

  const blockClass = `wp-block ${block.name.replace("/", "-")} style-carousel`
  const showAllClass = `showAllLink ${block.subHeading ? 'hasSubheading' : 'noSubheading'}`

  if (city == null || city == undefined) {
    //console.log('DEBUG: city is null ', block);
    return null
  }

  return (
    <div className={blockClass}>
      <div className='container heading'>
        {block.heading
          ? <h3><a id={hash}>{block.heading}</a></h3>
          : <h3>Featured</h3>}

        {block.subHeading && (
          <div
            className="subHeading"
            dangerouslySetInnerHTML={{ __html: block.subHeading }}
          />
        )}

        {block.showAll &&
          <a
            className={showAllClass}
            href={`/features${searchQuery}`}>
            <span>{showAllText}</span>
            <span>
              <MdArrowForward
                size={16}
                title={showAllText} />
            </span>

          </a>
        }
      </div>

      {isLoading
        ? cardsLoading
        : (
          <Suspense fallback={cardsLoading}>
            <Slider autoHeight={false} data={posts} theme='light' has_header={block.heading} />
          </Suspense>)
      }
    </div>
  )
}

// TODO: Move to utils or make it a hook
export const cardFromPost = (post, city = null) => {

  let image = PlaceholderImage

  // Make GraphQL and REST fields the same
  if (post.title.rendered) post.title = post.title.rendered

  // Set images
  if (post.featuredImage && post.featuredImage.node) {
    //console.log(`TODO: Replace featured image `, post.featuredImage);
    image = post.featuredImage.node.mediaItemUrl
  }
  if (post.featured_media_src_url) {
    image = post.featured_media_src_url
  }

  return <PostSingCard
    style="flat"
    orientation="vertical"
    url={post.link.replace(/^(?:\/\/|[^/]+)*/, "")}
    title={post.title}
    city={city}
    category={post.category ? post.category : 'Guide'}
    imageSrc={image}
    contentToAnalyze={post.content.rendered ? post.content.rendered : undefined}
  />
}

export default PostCardsBlock
